import React, { useEffect, useRef, useState } from "react";
import Modal from "~/components/atoms/Modal";
import SelectionPopover from "~/components/atoms/SelectionPopover";
import QaReviewCommentForm, {
  CommentCallbackResponse,
  QaArticleEvaluationCallbackData,
  QaCommentCallbackData,
  QaResizeImageCallbackData,
} from "~/components/modules/QaReviewCommentForm/QaReviewCommentForm";
import useSelection from "~/hooks/useSelection";
import { showToast } from "~/lib/toast/showToast";
import { QuestionAnswer } from "~/models/questionAnswer";
import { QaArticleEvaluation } from "~/models/questionAnswer/articleEvaluation";
import {
  QaReviewComment,
  QaReviewCommentMode,
} from "~/models/questionAnswer/reviewComment";
import CommentAndEvaluation from "./CommentAndEvaluation";
import CommentAndResize from "./CommentAndResize";

interface Props {
  onResizeImage?: (data: QaResizeImageCallbackData) => Promise<string>;
  onSubmitQaArticleEvaluation?: (
    data: QaArticleEvaluationCallbackData
  ) => Promise<CommentCallbackResponse>;
  onSubmitQaReviewComment?: (
    data: QaCommentCallbackData
  ) => Promise<CommentCallbackResponse>;
  questionAnswer: QuestionAnswer;
  reviewMode?: QaReviewCommentMode;
  targetElementRef: React.RefObject<HTMLElement>;
  targetQaField: Exclude<QaReviewComment["targetQaField"], "overview">;
}

const QaReviewCommentFormTrigger: React.VFC<Props> = ({
  onResizeImage,
  onSubmitQaArticleEvaluation,
  onSubmitQaReviewComment,
  questionAnswer,
  reviewMode,
  targetElementRef,
  targetQaField,
}) => {
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [showResizeItems, setShowResizeItems] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const { elements, text } = useSelection();
  const lineRef = useRef<number | undefined>(undefined);
  const textRef = useRef<string | undefined>(undefined);
  const imgSrcRef = useRef<string | undefined>(undefined);
  const [evaluationType, setEvaluationType] = useState<
    QaArticleEvaluation["type"] | null
  >(null);

  const onClickActionButton = () => {
    lineRef.current = calculateLine(elements, text);
    textRef.current = text;
    // Support only one image for simplicity.
    const imgElems = elements.filter((elm) => elm.nodeName === "IMG");
    if (imgElems.length > 0) {
      imgSrcRef.current = imgElems[0].getAttribute("src") ?? undefined;
    }
    window.getSelection()?.removeAllRanges();
  };

  const handleTriggerClick = () => {
    onClickActionButton();
    setEvaluationType(null);
    setIsCommentModalOpen(true);
  };

  const handleRequestClose = () => {
    lineRef.current = undefined;
    textRef.current = undefined;
    imgSrcRef.current = undefined;
    setEvaluationType(null);
    setIsCommentModalOpen(false);
  };

  const onClickEvaluation = ({
    evaluationType,
  }: {
    evaluationType: QaArticleEvaluation["type"];
  }) => {
    onClickActionButton();
    if (
      lineRef.current === undefined ||
      (!textRef.current && !imgSrcRef.current)
    ) {
      showToast("エラーが発生しました。再度お試しください");
      return;
    }
    setEvaluationType(evaluationType);
    setIsCommentModalOpen(true);
  };

  const onClickEvaluationType = ({
    evaluationType,
  }: {
    evaluationType: QaArticleEvaluation["type"] | null;
  }) => {
    setEvaluationType(evaluationType);
  };

  const onClickSize = async (size?: number) => {
    if (elements[0] instanceof HTMLImageElement) {
      setIsResizing(true);
      const imageSrc = elements[0].src;
      // modify image width in markdown
      const resizedImageUrl = await onResizeImage?.({
        imageAlt: elements[0].alt,
        imageSrc,
        size,
      });

      elements[0].src = resizedImageUrl ?? imageSrc;
      setIsResizing(false);
    }
  };

  useEffect(() => {
    setShowResizeItems(
      reviewMode !== "forNormalUser" &&
        targetQaField === "answerContent" &&
        elements.length === 1 &&
        elements[0].nodeName === "IMG"
    );
    setIsResizing(false);
  }, [elements, reviewMode, targetQaField]);

  return (
    <>
      <SelectionPopover
        disabled={isCommentModalOpen}
        targetElementRef={targetElementRef}
      >
        {showResizeItems ? (
          <CommentAndResize
            imageSrc={(elements[0] as HTMLImageElement).src}
            isResizing={isResizing}
            onClickSize={onClickSize}
            onClickComment={handleTriggerClick}
          />
        ) : (
          <CommentAndEvaluation
            onClickEvaluation={onClickEvaluation}
            onClickComment={handleTriggerClick}
            showEvaluation={!!onSubmitQaArticleEvaluation}
          />
        )}
      </SelectionPopover>

      <Modal isOpen={isCommentModalOpen} onClose={handleRequestClose}>
        {lineRef.current !== undefined &&
        (textRef.current || imgSrcRef.current) ? (
          <QaReviewCommentForm
            evaluationType={evaluationType}
            line={lineRef.current}
            onClickEvaluationType={onClickEvaluationType}
            onSubmitQaArticleEvaluationCallback={onSubmitQaArticleEvaluation}
            onSubmitQaReviewCommentCallback={onSubmitQaReviewComment}
            onSuccess={() => setIsCommentModalOpen(false)}
            questionAnswer={questionAnswer}
            reviewMode={reviewMode}
            targetImgSrc={imgSrcRef.current}
            targetQaField={targetQaField}
            targetText={textRef.current}
            withTalkRoomForm={false}
          />
        ) : (
          "エラーが発生しました。もう一度選択し直してください"
        )}
      </Modal>
    </>
  );
};

function calculateLine(elements: Element[], text: string): number {
  const breaks = calculateLineBreaks(elements, text);
  return (
    (elements
      .map((elm) => elm.getAttribute("data-s-line"))
      // Filter out invalid attributes (e.g. <br> doesn't have data-s-line)
      .filter((attr) => attr !== null)
      .map((str) => Number(str))
      // Sort to get the first one in the selected range.
      .sort((a, b) => a - b)
      .shift() ?? 0) + breaks
  );
}

// FIXME: This function is not always correct.
// 1: not supporting multiple elements in the range.
// 2: target text can appear multiple places.
function calculateLineBreaks(elements: Element[], text: string): number {
  if (
    elements.length === 0 ||
    !Array.from(elements[0].childNodes).some((elm) => elm.nodeName === "BR")
  ) {
    return 0;
  }
  const innerHTML = elements[0].innerHTML;
  const brCount = (innerHTML.match(/(<br>|<br >|<br\/>|<br \/>)/g) || [])
    .length;
  const brIndex = innerHTML.lastIndexOf("<br>");
  // Relying on simple text search is NOT always correct,
  // since the target text can appear multiple places.
  const textIndex = innerHTML.indexOf(text);
  return textIndex > brIndex ? brCount : 0;
}

export default QaReviewCommentFormTrigger;
